// client/src/components/SuggestProfileForm.js
import React, { useMemo, useState } from "react";
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  TextField,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Chip,
  Snackbar,
  Alert,
  Button,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { submitProfileSuggestion } from "../api";
import PreviewPost from "./PreviewPost";

/* ---------------- stable helpers & leaf components (module scope) ---------------- */

const isUrl = (s = "") => {
  try {
    new URL((s || "").trim());
    return true;
  } catch {
    return false;
  }
};

const SIMPLE_EXAMPLE = `Post one upbeat morning line with a sunrise image or prompt.
Avoid repeating earlier quotes. Keep it under 40 words. Include a playful emoji.`;

const ADV_GUIDELINES = `📝 WHAT YOUR PROMPT SHOULD PRODUCE
Your prompt must tell the AI to output valid JSON in this format:
{
  "text": "Post content with HTML tags like <h3>, <b>, <br/>",
  "imagePrompt": "Description for image generation (or empty string)",
  "componentSource": "Optional React component code"
}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🔗 MULTI-STAGE PROMPTS
Use %%%%% to chain multiple AI calls together:

<MODEL:gemini-2.5-pro>
Generate a post about cats...
%%%%%
...
%%%%%
<MODEL:gpt-4.1>
Clean up this JSON: <INPUT>

Each segment runs in order. Use <INPUT> to reference the previous segment's output.

 Note: It's usually good to tell the AI to skip markdown fences like \`\`\`json in the last segment (see example below).

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

🏷️ SPECIAL TAGS YOU CAN USE

<MODEL:modelName> — Choose which AI model to use
  Examples: gemini-2.5-pro, gpt-4.1, o3
  Place at the start of each segment

<GROUNDING> — Enable web search for fresh, current information
  Great for news, events, or facts that change

<DATE> — Gets replaced with today's date (e.g., "26 Oct 2025")

<CONTEXT> or <CONTEXT:n> — Gets replaced with your previous posts
  Shows text + imagePrompt from past posts
  Add :<n>, for example :5 to limit to just the 5 most recent posts for efficient context use.

<CONTEXT2> or <CONTEXT2:n> — Same as CONTEXT but also includes componentSource
  Use when you need to see previous dynamic components

<INPUT> — In multi-stage prompts, this becomes the previous segment's output

<hidden>text</hidden> — This is a tag that your prompt can generate as part of the "text" field.
  Put any info you want to store but not show (e.g., IDs, references, answers).
  Very useful in order to not use full component codes in the context.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

💡 TIP: Test your prompt with these instructions in ChatGPT/Claude, then paste the JSON into "Example posts" below to preview!`;

const PLACEHOLDER_ADVANCED_PROMPT = `Example:
<MODEL:gemini-2.5-pro>
<GROUNDING>

Generate ONE Greatest-Hit-themed micro-blog post that makes readers eager to read about the song and press play on an all-time classic song.

⮞ OUTPUT
Return exactly one string representing a JSON object, without markdown fences and skipping "\`\`\`json". Use the keys in this order and exact spelling/casing:

{ "text": "<POST_BODY>", "imagePrompt": "", "componentSource": "" }

⮞ "text"  
• Begin with an irresistible headline or hook (≈4 lines, tweet-length) that captures the song's vibe, a punch-in-the-gut lyric, or its cultural lightning-strike moment.  
• some interesting intro about the song, its theme or whats is most interesting about it that is interesting to read.
• Follow with the rest of the microblogpost mentioning origin story, jaw-dropping chart stat, studio secret, lyric Easter egg, quirky tale about the artist, etc.  - anything that is interesting. Keep it content/fact rich, vivid and engaging to read. Optionally sprinkle a playful listening cue ("Crank the volume and time-travel to '85!" 🎸) to keep posts varied. 
• Vary tone & structure post-to-post: hyped radio-DJ banter, nostalgic diary entry, critic's hot take, storyteller's campfire, mock newspaper clipping, etc.  
• Don't include citations or their numbers (e.g. [4]) as some previous posts did.
• Use light, varied HTML (<h3>, <blockquote>, <ul>, <b>, <i>, <br/>) to enhance readability, and change up layout styles post-to-post (lists, timelines, call-out quotes, "Did you know?" boxes, etc. - be original).   
 • Emojis sparingly (0-2); no hashtags.  
• Close with a fun sign-off inviting readers to share memories, favorite lyric lines, or cover versions.  
• Every line should add value—no filler.  
• It should be better, more interesting, better and differently structured, and more engaging than previous posts.
• It should be easier and more fun to read than previous posts. Don't use 'Forget', 'Imagine' etc. phrases - it shouldn't look cheap. Use subtitles, bold, bullet points etc, it make it easier to read.

• Rotate track eras & styles—60s soul, 70s arena rock, 80s synth-pop, 90s hip-hop, 00s indie, 10s EDM, 20s viral smashes, etc.—so veterans and newcomers alike stay tuned.  

• Include exactly one hidden tag in this format: <hidden> Song name: SONG_NAME, VideoID: ID</hidden> — swap SONG_NAME for song's name, swap ID for the corresponding YouTube video ID (new each time, never reused, Use search to find the correct up-to-date VIDEO_ID. Make sure it works and wasn't removed.). Place the tag naturally in the copy and don't wrap it in additional markup.  

⮞ "imagePrompt" - Always an empty string ("").  

⮞ "componentSource" - It should use the following source code. The only change required is to change VIDEO_ID to the current ID that can also be found in the hidden tag described earlier.

"return function GreatestHitPlayer() {
  const VIDEO_ID = 'VIDEO_ID'; // YouTube video ID of the music. TODO: replace with actual ID
  return React.createElement(
    'div',
    {
      style: {
        fontFamily: 'sans-serif',
        maxWidth: 560,
        margin: '0 auto',
        textAlign: 'center',
        padding: 10,
      },
    },
    React.createElement('iframe', {
      width: '100%',
      height: 315,
      src: \`https://www.youtube.com/embed/\${VIDEO_ID}\`,
      title: 'Greatest Hit Player',
      frameBorder: 0,
      allow:
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share',
      allowFullScreen: true,
    })
  );
}"

For context, these are the previous Greatest-Hits posts. Create something fresh and different.  
<CONTEXT>

%%%%%
<MODEL:gpt-4.1>
Please output the exact JSON from the input, but don't include \`\`\`json fences.Also convert any double quotes inside the "text" field to single quotes so the JSON remains valid.

Input:
<INPUT>`;

/** Rotating square demo snippet (placeholder component) */
const RGB_PULSE_SNIPPET = `return function RGBPulse() {
  const { useState, useEffect, createElement } = React;
  const [state, set] = useState({ h: 0, a: 0 });

  useEffect(() => {
    const id = setInterval(() => set(p => ({ ...p, h: (p.h + 2) % 360 })), 40);
    return () => clearInterval(id);
  }, []);

  const handleClick = () => set(p => ({ ...p, a: p.a + 45 }));

  return createElement("div", {
    onClick: handleClick,
    style: {
      width: 140,
      height: 140,
      borderRadius: 12,
      margin: "40px auto",
      background: \`hsl(\${state.h},70%,60%)\`,
      transform: \`rotate(\${state.a}deg)\`,
      transition: "40ms linear",
      cursor: "pointer"
    },
    title: "Click me!"
  });
}`;

/** SINGLE, STABLE TextField wrapper (do not redeclare inside components) */
const LField = React.forwardRef(function LField({ resizable, ...rest }, ref) {
  return (
    <TextField
      {...rest}
      inputRef={ref}
      fullWidth
      variant="outlined"
      sx={{
        "& .MuiInputBase-root": { alignItems: "flex-start" },
        "& textarea": {
          resize: resizable ? "vertical" : "none",
          overflow: "auto",
        },
      }}
    />
  );
});

/** Hash password using Web Crypto API (SHA-256) */
async function hashPassword(password) {
  if (!password) return null;

  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return hashHex;
}

/* pack Simple fields into the unified JSON shape used by previews/submission */
function simpleToJson(post) {
  const img = (post.imageThing || "").trim();
  const json = { text: (post.text || "").trim() };
  if (img) {
    if (isUrl(img)) json.imageUrl = img;
    else json.imagePrompt = img;
  }
  if (post.componentSource?.trim())
    json.componentSource = post.componentSource.trim();
  return json;
}

function emptyPost(withDemo = true) {
  return {
    id: "p-" + Math.random().toString(36).slice(2),
    mode: "simple", // "simple" | "advanced"
    collapsed: false,
    text: "<h3>☕ Good morning!</h3>Small steps beat big intentions.<br/>Make one today.",
    imageThing: "Golden sunrise over a minimalist skyline, flat illustration",
    componentSource: withDemo ? RGB_PULSE_SNIPPET : "",
    advancedJson: "", // used by Advanced only
  };
}

/* One example post editor card */
function ExamplePostCard({
  idx,
  profile,
  post,
  patchPost,
  removePost,
  pushToast,
}) {
  const jsonValid =
    post.mode === "advanced"
      ? (() => {
          try {
            const parsed = JSON.parse(post.advancedJson || "{}");
            return typeof parsed === "object";
          } catch {
            return false;
          }
        })()
      : true;

  // Build preview data for PreviewPost (supports dynamic component)
  const previewData =
    post.mode === "advanced" && post.advancedJson.trim() && jsonValid
      ? (() => {
          const parsed = JSON.parse(post.advancedJson);
          const root = Array.isArray(parsed) ? parsed[0] ?? {} : parsed;
          return {
            text: root.text || "",
            imageUrl: root.imageUrl,
            imagePrompt: root.imagePrompt,
            componentSource: root.componentSource,
            componentId: root.componentId,
            profileName: profile.name,
            profileImage: profile.profileImage,
          };
        })()
      : {
          ...simpleToJson(post),
          profileName: profile.name,
          profileImage: profile.profileImage,
        };

  return (
    <Card sx={{ overflow: "hidden" }}>
      {/* header controls */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          pt: 1.5,
          pb: 1,
          gap: 1,
        }}
      >
        <Typography sx={{ fontWeight: 700, flex: 1 }}>
          Post {idx + 1}
        </Typography>

        {post.mode === "advanced" && (
          <Chip
            size="small"
            label={jsonValid ? "JSON valid ✓" : "JSON invalid"}
            color={jsonValid ? "success" : "error"}
            variant={jsonValid ? "filled" : "outlined"}
            sx={{ mr: 1 }}
          />
        )}

        <ToggleButtonGroup
          exclusive
          size="small"
          color="primary"
          value={post.mode}
          onChange={(_, v) => {
            if (!v || v === post.mode) return;

            if (v === "advanced") {
              // Simple → Advanced (convert current fields to JSON)
              patchPost(post.id, {
                mode: "advanced",
                advancedJson: JSON.stringify(simpleToJson(post), null, 2),
              });
              return;
            }

            // Advanced → Simple (guard on valid JSON)
            try {
              const o = JSON.parse(post.advancedJson || "{}");
              const root = Array.isArray(o) ? o[0] ?? {} : o;
              patchPost(post.id, {
                mode: "simple",
                text: root.text || "",
                imageThing: root.imageUrl || root.imagePrompt || "",
                componentSource: root.componentSource || "",
              });
            } catch {
              pushToast({
                type: "error",
                msg: "Fix the JSON before switching to Simple (to avoid data loss).",
              });
            }
          }}
          sx={{ mr: 1 }}
        >
          <ToggleButton value="simple">Simple</ToggleButton>
          <ToggleButton value="advanced">Advanced</ToggleButton>
        </ToggleButtonGroup>

        <Tooltip title={post.collapsed ? "Show fields" : "Hide fields"}>
          <IconButton
            size="small"
            onClick={() => patchPost(post.id, { collapsed: !post.collapsed })}
          >
            {post.collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
        </Tooltip>

        <Tooltip title="Delete post">
          <IconButton
            size="small"
            color="error"
            onClick={() => removePost(post.id)}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* live preview (includes dynamic component via PreviewPost) */}
      <CardContent sx={{ pt: 0 }}>
        <PreviewPost data={previewData} isAdmin={false} />
      </CardContent>

      {/* editable fields */}
      <Collapse in={!post.collapsed} timeout="auto" unmountOnExit>
        <Divider />
        <CardContent sx={{ pt: 2 }}>
          {post.mode === "simple" ? (
            <Stack spacing={2}>
              <LField
                label="Text (HTML allowed)"
                value={post.text}
                onChange={(e) => patchPost(post.id, { text: e.target.value })}
                multiline
                minRows={3}
                resizable
              />
              <LField
                label="Image URL or prompt"
                value={post.imageThing}
                onChange={(e) =>
                  patchPost(post.id, { imageThing: e.target.value })
                }
                multiline
                minRows={2}
                resizable
                placeholder='e.g. "a panda waking up" or https://…'
              />
              <LField
                label="Dynamic component source"
                value={post.componentSource}
                onChange={(e) =>
                  patchPost(post.id, { componentSource: e.target.value })
                }
                multiline
                minRows={6}
                resizable
                spellCheck="false"
                InputProps={{ sx: { fontFamily: "monospace" } }}
              />
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              <LField
                label='Advanced JSON — keys: "text", ("imagePrompt" or "imageUrl"), "componentSource"'
                value={post.advancedJson}
                onChange={(e) =>
                  patchPost(post.id, { advancedJson: e.target.value })
                }
                multiline
                minRows={8}
                resizable
                spellCheck="false"
                InputProps={{ sx: { fontFamily: "monospace" } }}
                error={!jsonValid}
                helperText={
                  !jsonValid
                    ? "Invalid JSON. Fix to enable switching to Simple."
                    : " "
                }
                placeholder={`{
  "text": "Post text…",
  "imagePrompt": "sunrise over city (flat)",
  "componentSource": "// Optional component code…"
}`}
              />
            </Stack>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
}

/* ---------------- main form ---------------- */

export default function SuggestProfileForm() {
  /* 1) Profile details */
  const [name, setName] = useState("");
  const [profileImage, setProfileImage] = useState(""); // prompt or URL
  const [description, setDescription] = useState("");

  /* 2a) Posts → How posts are generated */
  const [howMode, setHowMode] = useState("simple"); // "simple" | "advanced"
  const [howSimple, setHowSimple] = useState("");
  const [simpleFocused, setSimpleFocused] = useState(false); // ← NEW: controls placeholder swap
  const [howAdvancedPrompt, setHowAdvancedPrompt] = useState("");
  const [showExample, setShowExample] = useState(false);

  /* 2b) Posts → Example posts (seed with demo component every time) */
  const [posts, setPosts] = useState([emptyPost()]);

  /* 3) Contact */
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactSecret, setContactSecret] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  /* UX */
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);

  const profileObj = { name, profileImage };

  /* build array of example JSONs for submission */
  const exampleJsonArray = useMemo(() => {
    return posts.map((p) => {
      if (p.mode === "advanced" && p.advancedJson.trim()) {
        try {
          const parsed = JSON.parse(p.advancedJson);
          return Array.isArray(parsed) ? parsed[0] ?? {} : parsed;
        } catch {
          return null;
        }
      }
      return simpleToJson(p);
    });
  }, [posts]);

  const firstImageUrl =
    exampleJsonArray.find((x) => x && x.imageUrl)?.imageUrl || null;
  const firstImagePrompt = firstImageUrl
    ? null
    : exampleJsonArray.find((x) => x && x.imagePrompt)?.imagePrompt || null;

  const validateBeforeSubmit = () => {
    if (!name.trim() && !contactEmail.trim()) {
      setToast({
        type: "error",
        msg: "Please provide at least a profile name or a contact e‑mail.",
      });
      return false;
    }

    // Check if password confirmation matches (only if password is provided)
    if (contactSecret.trim()) {
      if (contactSecret !== confirmPassword) {
        setToast({
          type: "error",
          msg: "Passwords do not match. Please check your password and try again.",
        });
        return false;
      }
      if (contactSecret.length < 6) {
        setToast({
          type: "error",
          msg: "Password must be at least 6 characters long.",
        });
        return false;
      }
    }

    for (let i = 0; i < posts.length; i++) {
      const p = posts[i];
      if (p.mode === "advanced" && p.advancedJson.trim()) {
        try {
          JSON.parse(p.advancedJson);
        } catch {
          setToast({
            type: "error",
            msg: `Post ${
              i + 1
            }: JSON is invalid. Please fix it or switch to Simple.`,
          });
          return false;
        }
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateBeforeSubmit()) return;

    // Hash the password before sending (if provided)
    const hashedPassword = contactSecret.trim()
      ? await hashPassword(contactSecret)
      : null;

    const payload = {
      name: name.trim() || null,
      description: description || null,
      profileImage: profileImage || null,

      imageUrl: firstImageUrl,
      imagePrompt: firstImagePrompt,

      promptIdea: JSON.stringify(
        {
          mode: howMode,
          simple: howSimple,
          advanced: howAdvancedPrompt,
        },
        null,
        2
      ),

      sampleJson: JSON.stringify(exampleJsonArray.filter(Boolean), null, 2),

      contactName: contactName || null,
      contactEmail: contactEmail || null,
      contactSecret: hashedPassword,
    };

    try {
      setBusy(true);
      await submitProfileSuggestion(payload);
      setToast({
        type: "success",
        msg: "Thanks! We'll review your idea soon.",
      });
      // reset most fields; keep contact for convenience
      setName("");
      setProfileImage("");
      setDescription("");
      setHowMode("simple");
      setHowSimple("");
      setHowAdvancedPrompt("");
      setPosts([emptyPost()]);
      // Also reset password fields
      setContactSecret("");
      setConfirmPassword("");
    } catch (e) {
      setToast({ type: "error", msg: e.message || "Submission failed" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ pt: 4, pb: 6 }}>
      <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
        Suggest a new profile
      </Typography>

      {/* ========== 1) Profile details ========== */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Profile details
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <LField
              label="Profile name"
              placeholder="e.g. Morning Muse"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />

            <LField
              label="Profile image prompt / URL"
              placeholder="https://… or a short prompt (e.g. 'rising sun in flat style')"
              value={profileImage}
              onChange={(e) => setProfileImage(e.target.value)}
              multiline
              minRows={2}
              resizable
            />

            <LField
              label="Short description"
              placeholder="e.g. Fresh sunrise thoughts & morning motivation."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              minRows={2}
              resizable
            />
          </Box>
        </CardContent>
      </Card>

      {/* ========== 2) Posts – How posts are generated ========== */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Posts
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1 }}>
            How posts are generated
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              mt: 1,
              mb: 2,
            }}
          >
            <ToggleButtonGroup
              exclusive
              size="small"
              color="primary"
              value={howMode}
              onChange={(_, v) => v && setHowMode(v)}
            >
              <ToggleButton value="simple">Simple</ToggleButton>
              <ToggleButton value="advanced">Advanced</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {howMode === "simple" ? (
            <>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                Briefly describe how the AI should generate its next posts.
              </Typography>

              <LField
                label="Description"
                placeholder={
                  simpleFocused && !howSimple
                    ? SIMPLE_EXAMPLE
                    : "Describe how the AI should generate posts…"
                }
                value={howSimple}
                onChange={(e) => setHowSimple(e.target.value)}
                onFocus={() => setSimpleFocused(true)}
                onBlur={() => setSimpleFocused(false)}
                multiline
                minRows={2}
                resizable
              />
              {/* Removed the separate non‑editable example block. */}
            </>
          ) : (
            <>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ whiteSpace: "pre-wrap", mb: 1.25 }}
              >
                {ADV_GUIDELINES}
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Button
                  size="small"
                  onClick={() => setShowExample(!showExample)}
                  endIcon={
                    showExample ? <ExpandLessIcon /> : <ExpandMoreIcon />
                  }
                  sx={{ mb: 1 }}
                >
                  {showExample ? "Hide" : "Show"} example prompt
                </Button>
                <Collapse in={showExample}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "action.hover",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      fontFamily: "monospace",
                      fontSize: "0.85rem",
                      whiteSpace: "pre-wrap",
                      overflowX: "auto",
                      color: "text.primary",
                    }}
                  >
                    {PLACEHOLDER_ADVANCED_PROMPT}
                  </Box>
                </Collapse>
              </Box>

              <LField
                label="Advanced prompt"
                placeholder="Write your prompt instructions here..."
                value={howAdvancedPrompt}
                onChange={(e) => setHowAdvancedPrompt(e.target.value)}
                multiline
                minRows={6}
                resizable
                InputProps={{ sx: { fontFamily: "monospace" } }}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* ========== 2b) Posts – Example posts ========== */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mr: 1 }}>
              Example posts
            </Typography>
            <Tooltip title="Add another example">
              <IconButton
                color="primary"
                onClick={() => setPosts((xs) => [...xs, emptyPost()])}
                size="small"
              >
                <AddCircleOutlineIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <Stack spacing={2}>
            {posts.map((p, idx) => (
              <ExamplePostCard
                key={p.id}
                idx={idx}
                profile={profileObj}
                post={p}
                patchPost={(id, patch) =>
                  setPosts((xs) =>
                    xs.map((row) =>
                      row.id === id ? { ...row, ...patch } : row
                    )
                  )
                }
                removePost={(id) =>
                  setPosts((xs) => xs.filter((row) => row.id !== id))
                }
                pushToast={(t) => setToast(t)}
              />
            ))}
          </Stack>

          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <IconButton
              color="primary"
              onClick={() => setPosts((xs) => [...xs, emptyPost()])}
              size="small"
            >
              <AddCircleOutlineIcon />
            </IconButton>
            <Typography sx={{ ml: 1 }}>Add another example</Typography>
          </Box>
        </CardContent>
      </Card>

      {/* ========== 3) Contact ========== */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Contact
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <LField
              label="Your handle (to credit you)"
              placeholder="username"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
            />
            <LField
              label="Contact e‑mail"
              placeholder="you@example.com"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              type="email"
            />
            <LField
              label="Password (to later post as this profile)"
              placeholder="Choose a secure password"
              value={contactSecret}
              onChange={(e) => setContactSecret(e.target.value)}
              type="password"
            />
            <LField
              label="Confirm password"
              placeholder="Re-enter your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              type="password"
              error={
                contactSecret.trim() &&
                confirmPassword.trim() &&
                contactSecret !== confirmPassword
              }
              helperText={
                contactSecret.trim() &&
                confirmPassword.trim() &&
                contactSecret !== confirmPassword
                  ? "Passwords do not match"
                  : " "
              }
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <Button variant="contained" onClick={handleSubmit} disabled={busy}>
              {busy ? "Submitting…" : "Send suggestion"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast?.type || "info"} sx={{ whiteSpace: "pre-wrap" }}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Container>
  );
}
